/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import axios from 'axios';
import { SecurityHelper } from 'sarsaparilla';
import { gnoment } from 'shared-ui/gnoment';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';

import {
    loadPermitContent,
    permitContentSelector,
} from '../../shared-components/slices/permitContent';

import * as types from '../constants';
import { printBlob, salesChannelTypes, getIssuanceDivision } from '../utils/utilsNew';
import * as enrichment from '../utils/enrich-backend-data';
import {
    RECAPTCHA_ERROR,
    RECAPTCHA_ERROR2,
    RECAPTCHA_CHALLENGE_FAILED,
    RECAPTCHA_CHALLENGE_REQUIRED,
    RECAPTCHA_BAD_FINGERPRINT,
} from '../../shared-components/constants/recaptcha';

const { PERMIT_ENDPOINT_V2, PDF_ENDPOINT, IS_LEGACY_PERMIT_V2 } = types;

function prepareIssuanceDate(date) {
    const normalizedDate = gnoment.isGnoment(date)
        ? date.clone().utc(true)
        : gnoment.utc(date);

    return normalizedDate.startOf('day').toISOString();
}

export function prepareIssuance(issuanceObj) {
    const issuance = { ...issuanceObj };

    if (issuance.start_date) {
        issuance.start_date = prepareIssuanceDate(issuance.start_date);
    }

    if (issuance.end_date) {
        issuance.end_date = prepareIssuanceDate(issuance.end_date);
    }

    if (issuance.lottery_preferences) {
        issuance.lottery_preferences = issuance.lottery_preferences.map((pref) => {
            const newPref = { ...pref };
            if (gnoment.isGnoment(newPref.preferred_date)) {
                const formattedDate = newPref.preferred_date
                    .utc(true)
                    .startOf('day')
                    .toISOString();
                newPref.preferred_date = formattedDate;
            }
            if (gnoment.isGnoment(newPref.preferred_range_start)) {
                const formattedDate = newPref.preferred_range_start
                    .utc(true)
                    .startOf('day')
                    .toISOString();
                newPref.preferred_range_start = formattedDate;
            }
            if (gnoment.isGnoment(newPref.preferred_range_end)) {
                const formattedDate = newPref.preferred_range_end
                    .utc(true)
                    .startOf('day')
                    .toISOString();
                newPref.preferred_range_end = formattedDate;
            }
            return newPref;
        });
    }

    if (issuance.group_info) {
        issuance.group_info = issuance.group_info.map((ind) => ({
            ...ind,
            ...(!ind.start_date && { start_date: issuance.start_date }),
            ...(!ind.end_date && { end_date: issuance.end_date }),
        }));
    }

    issuance.sales_channel_type = salesChannelTypes.get(types.ONLINE);
    return issuance;
}

export function fetchPermitReservationAlert(reservationId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permits/account/alerts/unread`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(({ data }) => {
                const alerts = data.payload;
                if (alerts) {
                    const al = alerts.find(
                        ({ is_read: isRead, alert_body: alertBody }) =>
                            !isRead && alertBody?.href.indexOf(reservationId) > 0
                    );
                    dispatch({
                        type: types.GET_PERMIT_RESERVATION_ALERT,
                        payload: al,
                    });
                }
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_PERMIT_RESERVATION_ALERT_ERROR,
                        payload: 'error while getting reservation alert',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchPermit(permitId) {
    return (dispatch, getState) => {
        // For NON LEGACY_PERMITS, it just needs loadPermitContent
        // For LEGACY_PERMITS, it needs the original PERMIT_ENDPOINT and joins
        //   the content from loadPermitContent
        const permitMapping = permitContentSelector.permitMapping(getState());
        const apiCalls = [dispatch(loadPermitContent(permitId))];
        if (IS_LEGACY_PERMIT_V2(permitId, permitMapping)) {
            apiCalls.push(axios.get(PERMIT_ENDPOINT_V2(permitId, permitMapping)));
        }

        return Promise.all(apiCalls)
            .then(([pcRes, pRes]) => {
                const pcPayload = cloneDeep(pcRes.payload);
                const { payload: pPayload } = pRes.data;
                const permit = pRes
                    ? enrichment.loadContentIntoPermit(pPayload, pcPayload)
                    : pcPayload;

                const { rules } = permit;

                rules.forEach((rule, index) => {
                    const newRule = { ...rule };
                    if (
                        types.DATE_TIME_RULES.find((dateRule) => dateRule === rule.name)
                    ) {
                        newRule.gnoment = gnoment.unix(newRule.value).utc();
                        newRule.string = newRule.gnoment.format(
                            types.AVAILABILITY_KEY_MAP_FORMAT
                        );
                    }
                    rules[index] = newRule;
                });

                const enrichedPermit = enrichment.transformPermit(permit);

                // Need to halve equipment weights if we are getting pere marquette
                if (permitId === types.PERE_MARQUETTE_ID) {
                    enrichedPermit.equipments.forEach((e) => {
                        e.weight *= 0.5;
                    });
                }

                dispatch({
                    type: types.GET_PERMIT,
                    payload: Object.assign(enrichedPermit, { indexed_rules: {} }),
                });
            })
            .catch((err) => {
                if (get(err, 'response.data')) {
                    dispatch({
                        type: types.GET_PERMIT_ERROR,
                        payload: 'error while fetching data',
                        error: err.response.data,
                    });
                }
            });
    };
}

export function fetchAggregateSeason(permitId, forLottery = false) {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/permits/${permitId}/getAggregateSeason?for_lottery=${forLottery}`
            )
            .then((success) => {
                dispatch({
                    type: types.GET_AGGREGATE_SEASON_RULES,
                    payload: success.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_AGGREGATE_SEASON_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchLotteries(permitId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/lottery/available`)
            .then((response) => {
                const lotteriesForPermit = get(response.data, 'lotteries', []).filter(
                    (lottery) =>
                        lottery.inventory_type === 'permit' &&
                        lottery.inventory_id === permitId
                );

                dispatch({ type: types.GET_LOTTERIES, payload: lotteriesForPermit });
            })
            .catch((error) => {
                // if there is an error on the payload (ie: from lottery service), use it. Otherwise, use the top level error
                const generalMessage = get(error, 'response.data.error');
                const message = get(error, 'response.data.payload.error', generalMessage);
                dispatch({
                    type: types.GET_LOTTERIES_ERROR,
                    payload: 'error while fetching lotteries',
                    error: message,
                });
            });
    };
}

export function fetchDailyLotteries(permitId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permits/${permitId}/lotteries/daily`)
            .then((response) => {
                dispatch({
                    type: types.GET_LOTTERIES_DAILY,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_LOTTERIES_DAILY_ERROR,
                    error: get(
                        error,
                        'response.data',
                        'There was an error trying to fetch the scheduled lotteries'
                    ),
                });
            });
    };
}

export function fetchLottery(lotteryId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permits/0/lotteries/${lotteryId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({
                    type: types.GET_LOTTERY,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_LOTTERY_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function verifyLottery(permitId, lotteryId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permits/${permitId}/lotteries/verify/${lotteryId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({
                    type: types.VERIFY_LOTTERY,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.VERIFY_LOTTERY_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function postIssuanceUrl(url, issuance, doRedirect, recaptcha = {}) {
    // We need to double our equipment weights before sending to the backend if we're pere marquette
    if (issuance.permitId === types.PERE_MARQUETTE_ID) {
        issuance.equipment.forEach((e) => {
            e.weight *= 2;
        });
    }

    const request = {
        gate_a: recaptcha,
        issuance,
    };

    return (dispatch) =>
        axios
            .post(url, JSON.stringify(request), {
                headers: {
                    Authorization: SecurityHelper.getAuthHeader(),
                    'Content-Type': 'application/json',
                },
            })
            .then((response) => {
                // A 2xx from this endpoint unambiguously means success.
                dispatch({ type: types.POST_ISSUANCE, payload: response.data.payload });
                if (doRedirect) window.location.assign(`/cart`);
                return Promise.resolve(response);
            })
            .catch((error) => {
                const errorMsg = error?.response?.data;

                // A borderline v3 score comes back as "additional challenge
                // required". Re-throw so the caller can pop the v2 checkbox; it
                // is a control signal, not a user-facing error.
                if (errorMsg?.error === RECAPTCHA_CHALLENGE_REQUIRED) {
                    dispatch({ type: types.POST_ISSUANCE_ERROR });
                    return Promise.reject(new Error(RECAPTCHA_CHALLENGE_REQUIRED));
                }

                if (errorMsg) {
                    if (errorMsg?.error === RECAPTCHA_CHALLENGE_FAILED) {
                        errorMsg.error = RECAPTCHA_ERROR;
                    } else if (
                        errorMsg?.error?.toLowerCase() === RECAPTCHA_BAD_FINGERPRINT
                    ) {
                        errorMsg.error = RECAPTCHA_ERROR2;
                    }
                    dispatch({
                        type: types.POST_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: errorMsg,
                    });
                }
                return Promise.reject(error);
            });
}

export function postIssuance(issuance, doRedirect, recaptcha = {}) {
    const url = `${process.env.API}/permits/${issuance.permit_id}/issuancesv2`;
    return postIssuanceUrl(url, issuance, doRedirect, recaptcha);
}

export function processBooking({ doRedirect = false, item, recaptcha }) {
    return (dispatch) => {
        return new Promise((resolve, reject) => {
            const preparedIssuance = prepareIssuance(item);
            dispatch(postIssuance(preparedIssuance, doRedirect, recaptcha))
                .then((resp) => {
                    resolve(resp);
                })
                .catch((err) => {
                    reject(err);
                });
        });
    };
}

export function updateIssuance(issuance) {
    const preparedIssuance = prepareIssuance(issuance);
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.UPDATE_ISSUANCE, payload: response.data.payload });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.UPDATE_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: error.response.data,
                    });
                }
            });
}

export function cancelIssuance(issuanceId, permitId) {
    // Note: do not actually need the body of the request (right now contains issuanceId)
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/cancel`,
                JSON.stringify(issuanceId),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.CANCEL_ISSUANCE, payload: response.data.payload });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.CANCEL_ISSUANCE_ERROR,
                        payload: 'error while cancelling reservation',
                        error: error.response.data,
                    });
                }
            });
}

export function addItem(itemType, item, doRedirect, recaptcha) {
    const preparedIssuance = prepareIssuance(item);
    return postIssuance(preparedIssuance, doRedirect, recaptcha);
}

export function saveFilters(filters) {
    return (dispatch) => {
        return dispatch({
            type: types.SAVE_FILTERS,
            payload: filters,
        });
    };
}

export function createLottery(lottery) {
    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${lottery.permit_id}/lotteries`,
                JSON.stringify(lottery),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({ type: types.POST_LOTTERY, payload: response.data.payload });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_LOTTERY_ERROR,
                        payload: 'error while creating lottery',
                        error: error.response.data,
                    });
                }
            });
}

export function sendCurrentDate(date) {
    return (dispatch) => {
        return dispatch({
            type: types.SEND_CURRENT_DATE,
            payload: date,
        });
    };
}

export function fetchIssuance(issuanceId, permitId) {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/account`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((success) => {
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: Object.assign(success.data.payload),
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
            });
    };
}

export function fetchUnpaidIssuance(issuanceId, permitId) {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/unpaid`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((success) => {
                dispatch({
                    type: types.GET_ISSUANCE,
                    payload: success.data.payload,
                });
                return success.data.payload;
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_ISSUANCE_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                        error_code: error.response.status,
                    });
                }
                throw error;
            });
    };
}

export function fetchRecarea(recareaId) {
    return (dispatch) => {
        return axios
            .get(
                `${process.env.API}/search?fq=entity_type%3Arecarea&fq=entity_id%3A${recareaId}`
            )
            .then((success) => {
                if (success.data && success.data.size > 0) {
                    dispatch({
                        type: types.GET_RECAREA,
                        payload: Object.assign(success.data.results),
                    });
                } else {
                    const error = 'No recreation area found';
                    dispatch({ type: types.GET_RECAREA_ERROR, payload: error, error });
                }
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_RECAREA_ERROR,
                    payload: 'error while fetching recarea data',
                    error,
                });
            });
    };
}

export function fetchCart() {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/cart/shoppingcart`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.GET_CART, payload: response.data });
            })
            .catch(() => {
                dispatch({
                    type: types.GET_CART_ERROR,
                    payload: 'error fetching cart',
                    error: true,
                });
            });
    };
}

export function cancelReservation(reservationId, navigate) {
    return (dispatch) => {
        return axios
            .delete(`${process.env.API}/cart/shoppingcart/${reservationId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.CANCEL_RESERVATION, payload: response.data });
                dispatch(fetchCart());
                navigate(-1);
            })
            .catch(() => {
                dispatch({
                    type: types.CANCEL_RESERVATION_ERROR,
                    payload: 'error canceling reservation',
                    error: true,
                });
            });
    };
}

export function fetchLotteryIssuances(lotteryId, statuses, alias) {
    const item = {
        lottery_id: lotteryId,
        statuses,
    };
    return (dispatch) => {
        return axios
            .post(
                `${process.env.API}/permits/0/lotteries/issuances`,
                JSON.stringify(item),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.GET_ISSUANCE_BY_STATUS,
                    payload: { [alias]: response.data.payload },
                });
            })
            .catch(() => {
                dispatch({
                    type: types.GET_ISSUANCE_BY_STATUS_ERROR,
                    payload: 'error fetching issuances',
                    error: true,
                });
            });
    };
}

export function notifyLotteryResult(lotteryId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permits/lotteries/${lotteryId}`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then(() => {
                dispatch({
                    type: types.NOTIFY_LOTTERY_RESULTS,
                    payload: 'Notify Lottery Results Success',
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.NOTIFY_LOTTERY_RESULTS_ERROR,
                    payload: 'error notify lottery results',
                    error: error.response.data,
                });
            });
    };
}

export function printIssuanceError(error) {
    return (dispatch) => {
        const payload = 'Error printing issuance PDF';
        let errorResponse = error?.response?.data;

        if (error?.response?.data && error?.request?.responseType === 'arraybuffer') {
            try {
                const decodedString = String.fromCharCode.apply(
                    null,
                    new Uint8Array(error.response.data)
                );
                const errObj = JSON.parse(decodedString);
                errorResponse = errObj?.error;
            } catch {
                // error should probably be sent to sentry. will default to default payload above.
            }
        }

        dispatch({
            type: types.GET_ISSUANCE_PDF_ERROR,
            payload,
            error: errorResponse || payload,
        });
    };
}

export function printIssuancePDF(permitId, issuanceId) {
    return (dispatch, getState) => {
        const permitMapping = permitContentSelector.permitMapping(getState());
        return (
            axios
                .get(PDF_ENDPOINT(permitId, issuanceId, permitMapping), {
                    headers: { Authorization: SecurityHelper.getAuthHeader() },
                    responseType: 'arraybuffer',
                })
                .then((response) => {
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    printBlob(blob);
                })
                // TODO - REMOVE THIS CATCH (possibly not needed). In some places we are not catching this on caller
                .catch((error) => {
                    dispatch(printIssuanceError(error));
                    throw error;
                })
        );
    };
}

export function getLottery(lotteryId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/lottery/${lotteryId}`)
            .then((response) => {
                dispatch({ type: types.GET_LOTTERY, payload: response.data.lottery });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_LOTTERY_ERROR,
                        payload: 'error while fetching data',
                        error: error.response.data,
                    });
                }
            });
    };
}

export function fetchCampsites(permitId) {
    return (dispatch) => {
        return axios
            .get(`${process.env.API}/permitcontent/${permitId}/campsites`, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            })
            .then((response) => {
                dispatch({ type: types.GET_CAMPSITES, payload: response.data.payload });
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_CAMPSITES_ERROR,
                    payload: 'error fetch campsites',
                    error: error.response && error.response.data,
                });
            });
    };
}

export function calculateFeeDifference(issuance) {
    const preparedIssuance = prepareIssuance(issuance);

    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/fees`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.POST_ISSUANCE_CALCULATE_FEES,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_CALCULATE_FEES_ERROR,
                        payload: 'error while calculating fee difference',
                        error: error.response.data,
                    });
                }
            });
}

export function fetchStayLimitRule(issuance) {
    const preparedIssuance = prepareIssuance(issuance);
    const isCommercial = SecurityHelper.isCommercialAccount();
    return (dispatch) =>
        axios
            .get(
                // eslint-disable-next-line max-len
                `${process.env.API}/permits/${preparedIssuance.permit_id}/divisions/${preparedIssuance.division_id}/rules/staylimit?start_date=${preparedIssuance.start_date}&is_commercial=${isCommercial}`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                if (get(response, 'data.payload.metadata')) {
                    const metadataToStore = JSON.parse(response.data.payload.metadata);
                    Object.keys(metadataToStore).forEach((key) => {
                        metadataToStore[key] /= types.DAYS_TO_SECS;
                    });
                    response.data.payload.metadata = metadataToStore;
                }
                dispatch({
                    type: types.GET_STAYLIMIT_RULE,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.GET_STAYLIMIT_RULE_ERROR,
                        payload: 'error while fetching staylimit rule',
                        error: error.response.data,
                    });
                } else if (error.message === 'Unexpected end of JSON input') {
                    dispatch({
                        type: types.GET_STAYLIMIT_RULE_ERROR,
                        payload: 'error while parsing metadata',
                        error: {},
                    });
                }
            });
}

// TODO deprecated API..  remove from both frontend and backend
export function postIssuanceCartless(issuance) {
    const preparedIssuance = prepareIssuance(issuance);

    return (dispatch) =>
        axios
            .post(
                `${process.env.API}/permits/${preparedIssuance.permit_id}/issuances/${preparedIssuance.id}/cartless`,
                JSON.stringify(preparedIssuance),
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.POST_ISSUANCE_CARTLESS,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.POST_ISSUANCE_CARTLESS_ERROR,
                        payload: 'error while posting issuance without payment',
                        error: error.response.data,
                    });
                }
            });
}

export function setIssuanceAfterChangeGroupSize(issuance) {
    return {
        type: types.SET_ISSUANCE_AFTER_CHANGE_GROUP_SIZE,
        payload: issuance,
    };
}

export function bypassToOrderConfirmation(issuance, permit) {
    const division = getIssuanceDivision(issuance, permit);
    const payload = {
        order_id: issuance.order_id,
        completed_reservations: [
            {
                order_details: {
                    description: permit.name,
                    img_url: permit.img_url,
                    location: division ? division.name : '',
                },
                order_id: issuance.order_id,
            },
        ],
        subtotal: 0,
        tax: 0,
        total: 0,
    };

    return (dispatch) => {
        dispatch({ type: types.FINALIZE_CHECKOUT, payload });
    };
}

export function clearIssuanceFromStore() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_ISSUANCE });
    };
}

export function verifyAlternate(permitId, issuanceId) {
    return (dispatch) =>
        axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/alternate/verify`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.VERIFY_ALTERNATE,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.VERIFY_ALTERNATE_ERROR,
                        payload: 'error while verifying alternate permit holder',
                        error: error.response.data,
                    });
                }
            });
}

export function markAlertAsRead(alertId) {
    return axios.put(
        `${process.env.API}/permits/account/alerts/${alertId}/read`,
        {},
        { headers: { Authorization: SecurityHelper.getAuthHeader() } }
    );
}

export function confirmAlternate(permitId, issuanceId) {
    return (dispatch) =>
        axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/alternate/confirm`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.CONFIRM_ALTERNATE,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.CONFIRM_ALTERNATE_ERROR,
                        payload: 'error while confirming alternate permit holder',
                        error: error.response.data,
                    });
                }
            });
}

export function declineAlternate(permitId, issuanceId) {
    return (dispatch) =>
        axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/alternate/decline`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.DECLINE_ALTERNATE,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                if (error.response && error.response.data) {
                    dispatch({
                        type: types.DECLINE_ALTERNATE_ERROR,
                        payload: 'error while declining alternate permit holder',
                        error: error.response.data,
                    });
                }
            });
}

export function fetchAccountEmployees() {
    return async (dispatch) => {
        try {
            const url = `${process.env.API}/permits/account/employees`;
            const result = await axios.get(url, {
                headers: { Authorization: SecurityHelper.getAuthHeader() },
            });
            dispatch({
                type: types.GET_ACCOUNT_EMPLOYEES,
                employees: result.data.payload,
            });
        } catch (error) {
            dispatch({
                type: types.GET_ACCOUNT_EMPLOYEES_ERROR,
                error: error.response.data.error,
            });
        }
    };
}

export function fetchIssuanceAccessInfo({ permitId, issuanceId }) {
    return (dispatch) =>
        axios
            .get(
                `${process.env.API}/permits/${permitId}/issuances/${issuanceId}/accessinfo`,
                { headers: { Authorization: SecurityHelper.getAuthHeader() } }
            )
            .then((response) => {
                dispatch({
                    type: types.GET_ISSUANCE_ACCESS_INFO,
                    payload: response.data.payload,
                });
            })
            .catch((error) => {
                dispatch({
                    type: types.GET_ISSUANCE_ACCESS_INFO_ERROR,
                    payload: error.response.data.error,
                });
            });
}

export function clearError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_ERROR });
    };
}

export function clearUpdateIssuanceError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_UPDATE_ISSUANCE_ERROR });
    };
}

export function clearFetchError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_FETCH_ERROR });
    };
}

export function clearCartError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_CART_ERROR });
    };
}

export function clearFetchIssuanceError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_FETCH_ISSUANCE_ERROR });
    };
}

export function clearAvailabilityError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_AVAILABILITY_ERROR });
    };
}

export function clearRecareaError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_RECAREA_ERROR });
    };
}

export function clearIssuanceError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_POST_ISSUANCE_ERROR });
    };
}

export function clearFetchLotteryError() {
    return (dispatch) => {
        dispatch({ type: types.CLEAR_FETCH_LOTTERY_ERROR });
    };
}

export function formValidationChange(path, error) {
    return (dispatch) =>
        dispatch({
            type: types.SET_VALIDATION_ERROR,
            payload: { path, error },
        });
}
